// Shared types
import { Product, AdditionalProduct, ServiceProduct, Map } from '@coldtec/cos-types';

/**
 *  Available store action for the products.
 */
export enum ProductsAction {
    /** Used to fetch and store the additional products. */
    FETCH_ADDITIONAL = 'products:fetch:additional',

    /** Used to fetch and store the service products. */
    FETCH_SERVICE = 'products:fetch:service',

    /** Used to sync the spaces of the request with the tool spaces. */
    SYNC_FROM_REQUEST = 'products:sync:fromRequest',

    /** Used to toggle the selected state of a product. */
    TOGGLE_SELECTED = 'products:toggle:selected',
}

/**
 *  Available store mutation for the products.
 */
export enum ProductsMutation {
    /** Used to store the additional products. */
    ADDITIONAL = 'products:additional',

    /** Used to store the service products. */
    SERVICE = 'products:service',

    /** Used to store the new selected state of a product. */
    TOGGLE_SELECTED = 'products:toggle:selected',
}

/**
 *  Available store getter for the products.
 */
export enum ProductsGetter {
    /** Used to get all the additional products, sorted by index. */
    ADDITIONAL = 'products:additional',

    /** Used to get all the service products, sorted by index. */
    SERVICE = 'products:service',

    /** Used to get all the products, unsorted */
    ALL = 'product:all',

    /** Used to get the count of all selected products. */
    SELECTED_COUNT = 'products:selected:count',
}

/**
 *  Available type of product.
 */
export enum ProductType {
    Additional = 'additional',
    Service = 'service',
}

/**
 *  Providing a wrapper for a product to contain original
 *  data, while able to add state for the tool.
 */
export interface ToolProduct<P = Product> {
    /** The type of product. */
    type: ProductType;

    /** The guid of the product. */
    guid: string;

    /** The data of the product it wraps. */
    data: P;

    /** The selected state of the product. */
    selected: boolean;

    /** The quantity of the product to add. */
    amount: number;

    /**
     *
     *  The price of the product.
     *
     *  (i) Set to the `price` or `unit_price` by
     *  default, but might be updated by a user for
     *  an internal, additional product.
     *
     */
    price: number | null;
}

/**
 *  Type of an Array of specific tool products.
 */
export type ToolProducts<P = Product> = Array<ToolProduct<P>>;

/**
 *  Holding the store's products state.
 */
export interface ProductsState {
    /** Holding the available additional products. */
    additional: ToolProducts<AdditionalProduct>;

    /** Holding the available service products. */
    service: ToolProducts<ServiceProduct>;

    /** Holding the returned product maps. */
    map: {

        /** Holding the map of all returned additional products. */
        additional: Map<AdditionalProduct>;

        /** Holding the map of all returned service products. */
        service: Map<ServiceProduct>;

    };
}
